import React from 'react';
import PropTypes from 'prop-types';
import { Grid, Row, Col } from 'react-flexbox-grid';
import { splitEvery, range } from 'ramda';

import { FormattedMessage } from 'react-intl';

import Dialog from 'material-ui/Dialog';
import { TEST_PHASES } from '../../containers/SDMTContainer';
import figures from './figures';

import { colors } from 'styles/provider';

import ActionButton from 'components/ActionButton';
import Checkbox from 'material-ui/Checkbox';

const fnSwitch = (prop, cases) => (cases[prop] ? cases[prop] : (cases['default'] && cases['default']) || null);

const SDMTSymbolDigitPair = ({ figure, digit, pairStyle = {}, onAnswer }) => (
  <Col style={{ ...pairStyle }} xs>
    <Row>
      <Col xs={12} style={{ padding: '0.5rem 0.5rem 0', border: '1px solid' }} onClick={() => onAnswer(digit)}>
        <img src={figures[figure]} />
      </Col>
      <Col xs={12} style={{ border: '1px solid' }} onClick={() => onAnswer(digit)}>
        <h3 style={{ fontWeight: 600, display: 'block', minHeight: '1.3em', lineHeight: '1.3em', minWidth: '1px' }}>
          {digit || null}
        </h3>
      </Col>
    </Row>
  </Col>
);

SDMTSymbolDigitPair.propTypes = {
  figure: PropTypes.string,
  digit: PropTypes.number,
  pairStyle: PropTypes.object,
  onAnswer: PropTypes.func,
};

const SDMTKey = ({ sdmtKey, onAnswer }) => (
  <Row>
    {sdmtKey.map(k => (
      <SDMTSymbolDigitPair key={k.figure} figure={k.figure} digit={k.expectedAnswer} onAnswer={onAnswer} />
    ))}
  </Row>
);

SDMTKey.propTypes = {
  sdmtKey: PropTypes.array,
};

const SHOWN_ITEM_NUMBER = 15;

const getCurrentItemSet = (items, currentItemId) => {
  const itemSets = splitEvery(SHOWN_ITEM_NUMBER, items);
  return itemSets[Math.floor(currentItemId / SHOWN_ITEM_NUMBER)];
};
const activeStyle = { backgroundColor: colors.clrAccent3 };
const rightAnswerStyle = { backgroundColor: colors.clrSDMTRight };
const wrongAnswerStyle = { backgroundColor: colors.clrSDMTWrong };

const getStyle = (item, currentId, answers, highlightAnswers = false) => {
  if (item.id === currentId) return activeStyle;
  if (!highlightAnswers) return {};
  if (answers[item.id]) return answers[item.id].answer === item.expectedAnswer ? rightAnswerStyle : wrongAnswerStyle;
  return {};
};

const SDMTResultRow = ({ testMode, useSpeechRecognition, items, currentItemId, answers, highlightAnswers = false }) => (
  <Row>
    {getCurrentItemSet(items, currentItemId).map(item => (
      <SDMTSymbolDigitPair
        key={`test_item_${item.id}`}
        pairStyle={getStyle(
          item,
          currentItemId,
          answers,
          (testMode === 'oral' && !useSpeechRecognition) || highlightAnswers,
        )}
        figure={item.figure}
        digit={
          testMode === 'oral' && !useSpeechRecognition
            ? item.expectedAnswer
            : answers[item.id]
              ? answers[item.id].answer
              : null
        }
      />
    ))}
  </Row>
);

SDMTResultRow.propTypes = {
  testMode: PropTypes.string,
  useSpeechRecognition: PropTypes.bool,
  items: PropTypes.array,
  currentItemId: PropTypes.number,
  answers: PropTypes.array,
  highlightAnswers: PropTypes.bool,
};

const SDMTKeyboard = ({ onAnswer }) => (
  <Row center="xs">
    {range(1, 10).map(n => (
      <Col xs={1} key={n}>
        <ActionButton primary border staticValue={n} onClick={() => onAnswer(n)} />
      </Col>
    ))}
  </Row>
);

SDMTKeyboard.propTypes = {
  onAnswer: PropTypes.func,
};

const SDMTOralKeyboard = ({ onAnswer }) => (
  <Row>
    <Col xs key={1}>
      <ActionButton primary border localeId="sdmt.true" onClick={() => onAnswer(10)} />
    </Col>
    <Col xs key={0}>
      <ActionButton primary border localeId="sdmt.false" onClick={() => onAnswer(0)} />
    </Col>
  </Row>
);

SDMTOralKeyboard.propTypes = {
  onAnswer: PropTypes.func,
};

const SDMTTestController = ({
  sdmtKey,
  testMode,
  useSpeechRecognition,
  items,
  currentItemId,
  answers,
  onAnswer,
  disabled = false,
  highlightAnswers = false,
}) => (
  <React.Fragment>
    <Row style={{ marginTop: '6rem' }} center="xs">
      <Col xs={8}>
        <SDMTKey sdmtKey={sdmtKey} onAnswer={onAnswer} />
      </Col>
    </Row>
    <Row style={{ marginTop: '6rem' }} center="xs">
      <Col xs={currentItemId > 104 ? 4 : items.length > 10 ? 12 : 8}>
        <SDMTResultRow
          testMode={testMode}
          useSpeechRecognition={useSpeechRecognition}
          items={items}
          highlightAnswers={highlightAnswers}
          currentItemId={currentItemId}
          answers={answers}
        />
      </Col>
    </Row>
    {!disabled && testMode === 'self' && (
      <Row style={{ marginTop: '3rem' }} center="xs">
        <Col xs={12}>
          <SDMTKeyboard onAnswer={onAnswer} />
        </Col>
      </Row>
    )}
    {!disabled && testMode === 'oral' && !useSpeechRecognition && (
      <Row style={{ marginTop: '6rem' }} center="xs">
        <Col xs={12}>
          <SDMTOralKeyboard onAnswer={onAnswer} />
        </Col>
      </Row>
    )}
  </React.Fragment>
);

SDMTTestController.propTypes = {
  sdmtKey: PropTypes.array,
  testMode: PropTypes.string,
  useSpeechRecognition: PropTypes.bool,
  items: PropTypes.array,
  currentItemId: PropTypes.number,
  answers: PropTypes.array,
  onAnswer: PropTypes.func,
  disabled: PropTypes.bool,
  highlightAnswers: PropTypes.bool,
};

const SDMTPracticePhase = props => (
  <Grid fluid>
    <Row center="xs">
      <Col xs={12}>
        <h5>
          <FormattedMessage id="sdmt.phase.practice" />
        </h5>
      </Col>
    </Row>
    <SDMTTestController {...props} />
  </Grid>
);

const SDMTInstructionPhase = props => (
  <Grid fluid>
    <Row center="xs" style={{ marginTop: '3rem' }}>
      <Col xs={12}>
        <h5>
          <FormattedMessage id="sdmt.phase.instructions" />
        </h5>
      </Col>
    </Row>
    <Row center="xs" style={{ marginTop: '2rem' }}>
      <Col xs={12}>
        <p>
          The test form is placed before the examinee and the following instructions are read aloud by the examiner
          (examiner reads only the boldface words):
        </p>
      </Col>
    </Row>
    <Row center="xs" style={{ marginTop: '2rem' }}>
      <Col xs={12}>
        <p>
          <b>
            Please look at these boxes at the top of the page. You can see that each box in the upper row has a little
            mark in it. Now look at the boxes in the row just underneath the marks. Each of the boxes under the marks
            has a number. Each of the marks in the top row is different, and under each mark in the bottom row is a
            different number.
          </b>
        </p>
      </Col>
    </Row>
    <Row center="xs" style={{ marginTop: '2rem' }}>
      <Col xs={12}>
        <p>
          <b>Now look at the next line of boxes</b> (examiner point to the line of boxes){' '}
          <b>
            just under the top two rows. Notice that the boxes on the top have marks, but the boxes underneath are
            empty. You are to fill each empty box with the number that should go there according to the way they are
            paired in the key at the top of the page and tell me what the number is. For example, if you look at the
            first mark, and then look at the key, you will see that the number 1 goes in the first box. So you can call
            out the number 1 for the first box. Now, what number should you put in the second box? Just call it out to
            me.
          </b>{' '}
          (Number 5) <b>That's right. So you would say "5" to me. What number goes in the third box?</b> (Number 2){' '}
          <b>
            Two, right. That's the idea. You are to fill each of the empty boxes with the numbers that should go in them
            according to the key and call out the numbers to me. Now for practice, tell me the numbers that fill the in
            the rest of the boxes until you come to the double line. When you come to the double line, stop.
          </b>
        </p>
      </Col>
    </Row>
    <Row center="xs" style={{ marginTop: '3rem' }}>
      <Col>
        <ActionButton primary border localeId="sdmt.beginPractice" onClick={props.beginPractice} />
      </Col>
    </Row>
    {props.testMode === 'oral' ? ( // speech regocnition disabled
      <Row>
        <Checkbox
          label={<FormattedMessage id="sdmt.useSpeechRecognition" />}
          checked={props.useSpeechRecognition}
          onCheck={(e, isChecked) => props.onSpeechRecognition(isChecked)}
        />
      </Row>
    ) : null}
  </Grid>
);

const SDMTPracticeEndPhase = props => (
  <Grid fluid>
    <Row center="xs">
      <Col xs={12}>
        <h5>
          <FormattedMessage id="sdmt.phase.practice" />
        </h5>
      </Col>
    </Row>
    <SDMTTestController {...props} />
    <Row center="xs" style={{ marginTop: '3rem' }}>
      <Col xs={12}>
        <p>
          <b>
            Now when I say "Go!" call out the numbers just like you have been doing until I say "Stop!". I will write
            the numbers down for you. When you come to the end of the first line, go quickly to the next line without
            stopping, and so on. If you make a mistake, tell me what you think the correct answer is. Do not skip any
            boxes and work as fast as you can. Ready? Go!
          </b>
        </p>
      </Col>
    </Row>
    <Row center="xs" style={{ marginTop: '2rem' }}>
      <Col xs={12}>
        <p>
          <i>Exactly 90 seconds from starting,</i> the examiner says: <b>Stop!</b>
        </p>
      </Col>
    </Row>
    <Row center="xs" style={{ marginTop: '3rem' }}>
      <Col>
        <ActionButton primary border localeId="sdmt.beginTest" onClick={props.startTest} />
      </Col>
    </Row>
  </Grid>
);

const SDMTTestTransitionPhase = props => (
  <Grid fluid>
    {/*<Row center="xs">
      <Col xs={12}>
        <h5>
          <FormattedMessage id="sdmt.phase.test" />
        </h5>
      </Col>
    </Row> */}
    <Row center="xs">
      <Col xs={12}>
        <h4>
          <FormattedMessage id="sdmt.test.counter" values={{ timeLeft: props.phaseTransitionTimeLeft }} />
        </h4>
      </Col>
    </Row>
    <SDMTTestController {...props} />
  </Grid>
);

const SDMTTestPhase = props => (
  <Grid fluid>
    {/*<Row center="xs">
      <Col xs={12}>
        <h5>
          <FormattedMessage id="sdmt.phase.test" />
        </h5>
      </Col>
    </Row> */}
    <Row center="xs">
      <Col xs={12}>
        <h4>GO!</h4>
      </Col>
    </Row>
    <SDMTTestController {...props} />
  </Grid>
);

const SDMTTestEndPhase = props => (
  <Grid fluid>
    <Row center="xs">
      <Col xs={12}>
        <h5>
          <FormattedMessage id="sdmt.phase.testEnd" />
        </h5>
      </Col>
    </Row>
    <Row center="xs" style={{ marginTop: '3rem' }}>
      <Col xs={12}>
        <h4>
          <FormattedMessage id="sdmt.results.info" />
        </h4>
      </Col>
    </Row>
    <Row center="xs" style={{ marginTop: '3rem' }}>
      <Col xs={12}>
        <p>
          <b>
            <FormattedMessage
              id="sdmt.results.text"
              values={{ totalAnswered: props.testAnswers.length || 0, correct: props.rightAnswerCount }}
            />
          </b>
        </p>
      </Col>
    </Row>
    <Row center="xs" style={{ marginTop: '3rem' }}>
      <Col xs={5}>
        <ActionButton localeId="sdmt.actions.cancel" onClick={props.cancelTest} />
      </Col>
      <Col xs={5}>
        <ActionButton secondary border localeId="sdmt.actions.save" onClick={props.saveResult} />
      </Col>
    </Row>
  </Grid>
);

const SDMTProcedure = ({
  cancelTest,
  beginPractice,
  onSpeechRecognition,
  saveResult,
  inceptionTS,
  currentPhase,
  sdmtKey,
  testMode,
  useSpeechRecognition,
  practiceItems,
  practiceItemId,
  practiceAnswers,
  onPracticeAnswer,
  testItems,
  testItemId,
  testAnswers,
  onTestAnswer,
  testTimeLeft,
  phaseTransitionTimeLeft,
  startTest,
  rightAnswerCount,
}) => {
  const devActions = [<ActionButton localeId="Cancel test" onClick={cancelTest} />];

  const instructionProps = {
    testMode,
    useSpeechRecognition,
    beginPractice,
    onSpeechRecognition,
  };

  const practiceProps = {
    sdmtKey,
    testMode,
    useSpeechRecognition,
    items: practiceItems,
    currentItemId: practiceItemId,
    answers: practiceAnswers,
    onAnswer: onPracticeAnswer,
    highlightAnswers: testMode === 'oral' && !useSpeechRecognition ? true : false,
  };

  const practiceEndProps = {
    ...practiceProps,
    disabled: true,
    highlightAnswers: true,
    startTest,
  };

  const testProps = {
    sdmtKey,
    testMode,
    useSpeechRecognition,
    items: testItems,
    currentItemId: testItemId,
    answers: testAnswers,
    onAnswer: onTestAnswer,
    highlightAnswers: testMode === 'oral' && !useSpeechRecognition ? true : false,
    phaseTransitionTimeLeft,
    testTimeLeft,
  };

  const testTransitionProps = {
    ...testProps,
    disabled: true,
  };

  const testEndProps = {
    rightAnswerCount,
    testAnswers,
    saveResult,
    cancelTest,
  };

  return (
    <Dialog
      modal
      open={!!inceptionTS}
      contentStyle={{ width: '90%', height: '800px', maxWidth: '1400px', color: '#222' }}
      bodyStyle={{ color: '#222' }}
      autoScrollBodyContent={true}
    >
      {fnSwitch(currentPhase, {
        [TEST_PHASES.INSTRUCTIONS]: <SDMTInstructionPhase {...instructionProps} />,
        [TEST_PHASES.PRACTICE]: <SDMTPracticePhase {...practiceProps} />,
        [TEST_PHASES.PRACTICE_END]: <SDMTPracticeEndPhase {...practiceEndProps} />,
        [TEST_PHASES.TEST_TRANSITION]: <SDMTTestTransitionPhase {...testTransitionProps} />,
        [TEST_PHASES.TEST]: <SDMTTestPhase {...testProps} />,
        [TEST_PHASES.TEST_ENDED]: <SDMTTestEndPhase {...testEndProps} />,
      })}
    </Dialog>
  );
};

SDMTProcedure.propTypes = {
  cancelTest: PropTypes.func,
  startTest: PropTypes.func,
  beginPractice: PropTypes.func,
  onSpeechRecognition: PropTypes.func,
  saveResult: PropTypes.func,

  onPracticeAnswer: PropTypes.func,
  onTestAnswer: PropTypes.func,
  currentPhase: PropTypes.string,
  phaseTransitionTimeLeft: PropTypes.number,
  rightAnswerCount: PropTypes.number,

  inceptionTS: PropTypes.number,
  practiceStartTS: PropTypes.number,
  practiceEndTS: PropTypes.number,
  testStartTS: PropTypes.number,
  testEndTS: PropTypes.number,
  testTimeLeft: PropTypes.number,

  practiceItemId: PropTypes.number,
  testItemId: PropTypes.number,

  sdmtKey: PropTypes.array,
  testMode: PropTypes.string,
  useSpeechRecognition: PropTypes.bool,
  practiceItems: PropTypes.array,
  practiceAnswers: PropTypes.array,
  testItems: PropTypes.array,
  testAnswers: PropTypes.array,
};

export default SDMTProcedure;
